import { type InstallHealthStatus } from '@electron/shared';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { type ModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { YStack } from '@/components/ui/layout/layout';
import { updateStore } from '@/stores/updateStore';
import { trackEvent } from '@/utils/analytics';

interface InstallRepairCopy {
  title: string;
  body: string;
  steps?: string[];
  primaryLabel: string;
}

const MOVE_PRIMARY_LABEL = 'Move and Restart';

const REINSTALL_STEPS = [
  'Click the button below. It opens the download page and quits Littlebird.',
  'Drag the new Littlebird into the Applications folder and replace the old one.',
  'Open Littlebird again from the Applications folder.',
];

const MOVE_FAILED_STEPS = [
  'Quit Littlebird using the button below.',
  'In Finder, drag Littlebird into the Applications folder. If it is already there, skip this step.',
  'Open Littlebird again from the Applications folder.',
];

function getReinstallCause(installHealth: InstallHealthStatus): string {
  return installHealth.issues.includes('wrongOwner')
    ? 'Littlebird was installed by a different account on this Mac, so your account cannot update it.'
    : 'Your account does not have write access to the Applications folder, so Littlebird cannot update itself there.';
}

function getRepairCopy(
  installHealth: InstallHealthStatus,
  isMoveRepair: boolean
): InstallRepairCopy {
  if (isMoveRepair) {
    return {
      title: 'Move Littlebird to Applications',
      body: 'Littlebird is not in your Applications folder, so it cannot update itself. Littlebird can move itself there for you. It will restart once and no password is needed.',
      primaryLabel: MOVE_PRIMARY_LABEL,
    };
  }

  return {
    title: 'Reinstall Littlebird',
    body: `${getReinstallCause(installHealth)} Reinstalling with a fresh copy may fix this:`,
    steps: REINSTALL_STEPS,
    primaryLabel: 'Download and Quit',
  };
}

function RepairSteps({ steps }: { steps: string[] }) {
  return (
    <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground-subtle">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
}

export const InstallRepairModal: ModalFC<{}> = observer(
  ({ open, onOpenChange }) => {
    const [isRepairing, setIsRepairing] = useState(false);
    const { installHealth, repairError } = updateStore;

    useEffect(() => {
      trackEvent('INSTALL_REPAIR_PROMPT_SHOWN');
    }, []);

    if (!installHealth || installHealth.repair === 'none') return null;

    const isMoveRepair = installHealth.repair === 'moveToApplications';
    const moveFailed = repairError !== null && isMoveRepair;
    const copy = getRepairCopy(installHealth, isMoveRepair);

    const handleOpenChange = (nextOpen: boolean) => {
      if (!nextOpen) {
        updateStore.dismissInstallRepair();
      }
      onOpenChange(nextOpen);
    };

    const handlePrimaryAction = async () => {
      if (moveFailed) {
        updateStore.quitApp();
        return;
      }
      if (isMoveRepair) {
        setIsRepairing(true);
        await updateStore.repairInstall();
        setIsRepairing(false);
        return;
      }
      void updateStore.downloadAndQuit();
    };

    return (
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        <Dialog.Content className="max-w-md">
          <Dialog.Header>
            <Dialog.Title className="text-lg">{copy.title}</Dialog.Title>
            <Dialog.Description className="text-foreground-subtle">
              {copy.body}
            </Dialog.Description>
          </Dialog.Header>

          <YStack className="gap-3">
            {copy.steps && <RepairSteps steps={copy.steps} />}

            {moveFailed && (
              <YStack className="gap-2 rounded-lg border border-border-strong bg-surface-subtle p-3">
                <p className="text-sm text-danger-11">
                  That did not work. You can move it yourself:
                </p>
                <RepairSteps steps={MOVE_FAILED_STEPS} />
                <p className="text-xs text-foreground-muted">{repairError}</p>
              </YStack>
            )}
          </YStack>

          <Dialog.Footer className="gap-3 pt-4">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Not Now
            </Button>
            <Button
              onClick={handlePrimaryAction}
              isLoading={isRepairing}
              loadingText={MOVE_PRIMARY_LABEL}
            >
              {moveFailed ? 'Quit Littlebird' : copy.primaryLabel}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);
